/* eslint-disable @typescript-eslint/naming-convention */

import { RecordAny } from '../../models/common.models';

export enum BookPlatformDto {
  VST = 'VST',
  CKS = 'CKS',
}

export enum OsmosisPaidStatusDto {
  PAID = 'paid',
  TRIAL = 'trial',
  EXPIRED = 'expired',
  NONE = 'none',
}

export enum OsmosisMarketDto {
  B2C = 'b2c',
  B2B = 'b2b',
}

export type EbookSsoUrlRequestDto = {
  vitalSourceId: string;
  bookPlatform: string;
  isbn: string;
  startPage: string;
  endPage: string;
  isOsmosis: boolean;
  isExcerptView: boolean;
  osmosisPaidStatus: string;
  osmosisMarket: string;
  chunkId: string;
  entryId: number;
  referenceId: number;
};

export type EbookCfiSsoUrlRequestDto = {
  vitalSourceId: string;
  isbn: string;
  cfiRange: string;
  isExcerptView: boolean;
  referenceId: number;
  entryId: number;
  isOsmosis: boolean;
  osmosisPaidStatus: string;
  osmosisMarket: string;
  bookPlatform: BookPlatformDto;
};

export type EbookSsoUrlResponseDto = {
  signInUrl: string;
  expires: string;
};

export enum PostProcessJobTypeDto {
  FILTER_RELEVANT_REFERENCES = 'FILTER_RELEVANT_REFERENCES',
  RETRIEVE_RELATED_CONTENT_IMAGES = 'RETRIEVE_RELATED_CONTENT_IMAGES',
  RETRIEVE_RELATED_CONTENT_VIDEOS = 'RETRIEVE_RELATED_CONTENT_VIDEOS',
  GENERATE_FLASHCARDS = 'GENERATE_FLASHCARDS',
  GENERATE_QUIZ_QUESTIONS = 'GENERATE_QUIZ_QUESTIONS',
  GENERATE_FOLLOW_UP_QUESTIONS = 'GENERATE_FOLLOW_UP_QUESTIONS',
}

export enum PostProcessJobStatusDto {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR',
  CANCELLED_TIMED_OUT = 'CANCELLED_TIMED_OUT',
  CANCELLED_ADMIN_REQUEST = 'CANCELLED_ADMIN_REQUEST',
}

export type PostProcessJobDto = {
  id?: number;
  entryId: number;
  chatId: number;
  jobType: PostProcessJobTypeDto;
  status: PostProcessJobStatusDto;
  errorMessage?: string;
  attributes?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
};

export type GeneratedQuizQuestionDataDto = {
  qtiData: {
    prompt: string;
    questionType: string;
    responseChoices: Record<string, string>;
    feedback: Record<string, string>;
    correctResponse: string[];
  };
};

export type GeneratedQuizQuestionDto = {
  id?: number;
  entryId: number;
  chatId: number;
  postProcessJobId: number;
  eolsAssessmentId: number;
  displayOrder?: number;
  questionData?: GeneratedQuizQuestionDataDto;
  createdAt?: string;
  updatedAt?: string;
};

export enum LibrarySortDto {
  ASC = 'asc',
  DESC = 'desc',
}

// What a library page (Quiz questions, Flashcards) has selected: filters, sort and segment window. The list,
// "View" (and, for quiz questions, "Take") all send these same values, so they always operate on the same set.
// createdFrom/createdTo are ISO-8601 UTC instants (the user's local start/end of day); NACS applies them inclusively.
// NACS sorts the matches (default newest first), then takes the 1-based inclusive startIndex..endIndex
// window of them (default the first through the last).
export type LibrarySelectionDto = {
  search?: string;
  isbn?: string;
  chatId?: number;
  entryId?: number;
  createdFrom?: string;
  createdTo?: string;
  sort?: LibrarySortDto;
  startIndex?: number;
  endIndex?: number;
};

export type LibraryQueryParamsDto = LibrarySelectionDto & {
  page?: number;
  pageSize?: number;
  // Returns the whole selection ("View") instead of one page
  unpaged?: boolean;
};

export type LibraryListResponseDto<T> = {
  content: T[];
  totalPages: number;
  currentPage: number;
  // Size of the segment window the table paginates over
  totalItems: number;
  // Items matching the filters before the segment window; the segment choices are built from it
  totalMatches: number;
  isLast: boolean;
};

export type QuizQuestionSelectionDto = LibrarySelectionDto;

export type QuizQuestionQueryParamsDto = LibraryQueryParamsDto;

export type QuizQuestionListItemDto = GeneratedQuizQuestionDto & {
  chatTitle: string;
  // The user message the question was generated from
  entryText: string;
};

export type QuizQuestionListResponseDto = LibraryListResponseDto<QuizQuestionListItemDto>;

export type CreatePracticeQuizRequestDto = QuizQuestionSelectionDto & {
  // Reorders the selected questions without changing which ones are in the quiz
  shuffle?: boolean;
};

export type CreatePracticeQuizResponseDto = {
  assessmentId: number;
};

export enum ChatContextTypeDto {
  COURSE_SECTION_ID = 'COURSE_SECTION_ID',
  EVAL = 'EVAL',
  ASSIGNMENT_ID = 'ASSIGNMENT_ID',
  IFRAME = 'IFRAME',
  OSMOSIS_USER = 'OSMOSIS_USER',
  SAI_USER = 'SAI_USER',
  // SAI_LESSON = 'SAI_LESSON', // TODO: Consider this if lessons are to remain isolated - waiting for product direction here
  PLP = 'PLP',
  BANK_COURSE = 'BANK_COURSE',
  VST = 'VST',
  // Value stays 'CKSA': the CK Assessment launcher and NACS-persisted entries already use it.
  SPARK = 'CKSA',
}

export enum ChatModelNameDto {
  gpt4 = 'gpt4',
  gpt35 = 'gpt35',
  titan = 'titan', // AWS Titan TG1 Large
  claude = 'claude', // Claude 2
}

export enum ChatTypeDto {
  FLORENCE_USER_CHAT = 'FLORENCE_USER_CHAT',
  BATCH_EVAL_CHAT = 'BATCH_EVAL_CHAT',
}

export enum ChatAttributes {
  isTestUser = 'isTestUser',
  isMasqueradeUser = 'isMasqueradeUser',
  isDirectAccessByFeatureFlag = 'isDirectAccessByFeatureFlag',
  emailDomain = 'emailDomain',
  userRole = 'userRole',
  brand = 'brand',
}

export type ChatDto = {
  id: number;
  createdAt: string;
  updatedAt: string;
  lastAccessed?: string;
  title: string;
  userId: number;
  contextId: string;
  contextType: ChatContextTypeDto;
  status: ChatStatusDto;
  entries: ChatEntryDto[];
  type: ChatTypeDto;
  modelName: ChatModelNameDto;
  attributes: Partial<Record<ChatAttributes, unknown>>;
  ecrId: string;
  userRole: ChatUserRoleDto;
}

export enum ChatStatusDto {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED'
}

export enum ChatEntryAuthorDto {
  USER = 'USER',
  BOT = 'BOT'
}

export enum ChatEntryTypeDto {
  EXPLAINER = 'EXPLAINER',
  MESSAGE = 'MESSAGE',
  SHELL = 'SHELL'
}

export enum ChatQueryIntentDto {
  COMMENT = 'COMMENT',
  QUESTION = 'QUESTION',
  TEST = 'TEST',
}

export enum OverridePromptType {
  chatbotCapabilities = 'chatbotCapabilities',
  classificationIsRelevant = 'classificationIsRelevant',
  classificationStudyContentGeneration = 'classificationStudyContentGeneration',
  contextResolution = 'contextResolution',
  primaryResponse = 'primaryResponse',
  primaryResponseTutorMode = 'primaryResponseTutorMode',
  studyContentGenerationResponse = 'studyContentGenerationResponse',
  declineResponseNoSearchResults = 'declineResponseNoSearchResults',
  postProcess = 'postProcess',
  declineResponse = 'declineResponse',
  respondWithoutCitations = 'respondWithoutCitations',
  rankAndFilterSearchResults = 'rankAndFilterSearchResults',
  followUpQuestions = 'followUpQuestions',
  relatedContentSummarization = 'relatedContentSummarization',
  relatedContentRateVectorResults = 'relatedContentRateVectorResults',
  flashcardAcknowledgment = 'flashcardAcknowledgment',
  flashcardGeneration = 'flashcardGeneration',
  tableContextResolution = 'tableContextResolution',
  tableRankAndFilterSearchResults = 'tableRankAndFilterSearchResults',
}

export type PromptOverrideConfigDto = Partial<{
  prompt: string;
  userChatItemTemplate: string;
  botChatItemTemplate: string;
  promptBookChunkItemTemplate: string;
  promptVideoItemTemplate: string;
  promptContentChunkItemTemplate: string;
  model: string;
  temperature: string;
  reasoning: string;
  provider: string;
  virtualKey: string;
  cerebusApiKey: string;
  config: string;
}>

export type SearchOverrideDto = Partial<{
  isbns: string[];
  isUseBaseSet: boolean;
}>

export type BaseEntryContextDto = {
  contextType: ChatContextTypeDto;
  pageContext: string;
  isTutorModeEnabled?: boolean;
}

export type PlpEntryContextDto = BaseEntryContextDto & {
  programType: string;
  taxons: string[];
}

export type OsmosisEntryContextDto = BaseEntryContextDto & {
  program: string;
  bookPlatform: BookPlatformDto;
  productCode: string;
}

export type SaiUserEntryContextDto = BaseEntryContextDto;

export type BankCourseEntryContextDto = BaseEntryContextDto;

export type IframeEntryContextDto = BaseEntryContextDto;

export type VstEntryContextDto = BaseEntryContextDto & {
  isbn: string;
}

export type SparkEntryContextDto = BaseEntryContextDto & {
  contextId: string;
  bookPlatform?: BookPlatformDto;
  program?: string;
}

export type PLPAppLinkContextDto = PlpEntryContextDto & {
  contextId: string;
};

export type OsmosisAppLinkContextDto = OsmosisEntryContextDto & {
  linkBackUrl: string;
  program: string;
  bookPlatform: BookPlatformDto;
  paidStatus: OsmosisPaidStatusDto;
  market: OsmosisMarketDto;
  productCode: string;
  productID: number;
  includeIframeVideoLink: boolean;
};

export type VstAppLinkContextDto = VstEntryContextDto & {
  contextId: string;
};

export type SparkAppLinkContextDto = SparkEntryContextDto & {
  contextType: ChatContextTypeDto.SPARK;
};

export type AppLinkContextDto = PLPAppLinkContextDto | OsmosisAppLinkContextDto | VstAppLinkContextDto | SparkAppLinkContextDto;

export type EntryContextDto =
  | PlpEntryContextDto
  | OsmosisEntryContextDto
  | SaiUserEntryContextDto
  | BankCourseEntryContextDto
  | IframeEntryContextDto
  | VstEntryContextDto
  | SparkEntryContextDto;

export type PromptOverrideDto = Partial<Record<OverridePromptType, PromptOverrideConfigDto>>

export type OverrideDto = {
  searchOverride: SearchOverrideDto;
  promptOverride: PromptOverrideDto;
}

export type ShellsDto = {
  message: string;
  filters: SearchOverrideDto;
  context: EntryContextDto;
  attributes: RecordAny;
  iframeContext: {
    iframeContextEntity: string; //"chapter",
    iframeContext: string; //"chapter",
    contentType: string; //"textbook",
    contentId: string; //"C123456",
    instanceId: string; //"INS789",
    contentItemType: string; //"section",
    contentItemId: string; //"SEC456"
  };
  promptOverride?: PromptOverrideDto;
  searchOverride?: SearchOverrideDto;
}

export enum ChatEntryStatus {
  THINKING = 'THINKING', // everything before stream
  STREAMING = 'STREAMING',
  POST_PROCESSING = 'POST_PROCESSING',
  COMPLETE = 'COMPLETE',
}

export enum FinalPromptType {
  DECLINE_RESPONSE_FAILED_CLASSIFICATION = 'DECLINE_RESPONSE_FAILED_CLASSIFICATION',
  DECLINE_RESPONSE_NO_SEARCH_RESULTS = 'DECLINE_RESPONSE_NO_SEARCH_RESULTS',
  PRIMARY_RESPONSE = 'PRIMARY_RESPONSE',
  PRIMARY_RESPONSE_TABLE_FALLBACK = 'PRIMARY_RESPONSE_TABLE_FALLBACK',
  PRIMARY_RESPONSE_TUTOR_MODE = 'PRIMARY_RESPONSE_TUTOR_MODE',
  MCQ_RESPONSE = 'MCQ_RESPONSE',
  FLASHCARD_ACKNOWLEDGMENT = 'FLASHCARD_ACKNOWLEDGMENT',
  QUIZ_QUESTION_ACKNOWLEDGMENT = 'QUIZ_QUESTION_ACKNOWLEDGMENT',
  RESPOND_WITHOUT_CITATIONS = 'RESPOND_WITHOUT_CITATIONS',
  CONTENT_RETRIEVAL_ACKNOWLEDGMENT = 'CONTENT_RETRIEVAL_ACKNOWLEDGMENT',
  ERROR = 'ERROR',
}

export type ChatEntryDto = {
  id: number; // db id
  message: string;
  filters: SearchOverrideDto;
  chatId: number;
  parentEntryId: number;
  index: number; // order in chat
  author: ChatEntryAuthorDto; // bot or user
  entryType: ChatEntryTypeDto; // message or maybe something like quiz in the future
  references: ChatReferenceDto[];
  relatedContent?: RelatedContentDto[]; // related content for this entry
  queryIntent: ChatQueryIntentDto;
  errorMessage?: string;
  totalInputTokenCount?: number;
  totalOutputTokenCount?: number;
  createdAt?: string;
  updatedAt?: string;
  finalPromptType: FinalPromptType;
  _status?: ChatEntryStatus;
  attributes: RecordAny;
  ebookEntitlements?: ChatEntitlementDto[]; // ebook entitlements from shells API response
  disciplineProfile?: string[]; // discipline profile from shells API response
  searchOverride?: SearchOverrideDto;
  promptOverride?: PromptOverrideDto; // admin prompt/model overrides echoed back on the shells API response
  postProcessJobs?: PostProcessJobDto[];
  context?: EntryContextDto;
}

export type ChatEntryEvaluationDto = {
  id: number; // db id
  userId: string;
  userType: EvaluationUserTypeDto;
  entryId: number;
  comment: string;
  overall: number;
  comprehension: number;
  correctness: number;
  completeness: number;
  harmfulness: number;
  accuracy: string;
  coherency: string;
  organization: string;
  relevancy: string;
  detail: string;
  entryFeedbackReferences: Partial<ChatReferenceEvaluationDto>[];
}

export type ChatReferenceEvaluationDto = {
  id: number; // db id
  chatEntryEvalId: number; // db id of ChatEntryEvaluationDto
  referenceId: number;
  overall: number;
}

export enum EvaluationUserTypeDto {
  CLIENT = 'CLIENT',
  EXPERT = 'EXPERT',
}

export enum ChatReferenceClassDto {
  CITATION = 'CITATION',
  RECOMMENDATION = 'RECOMMENDATION'
}

type ChatReferencePartDto = {
  uri: string;
  index: number;
  type: ChatReferenceTypeDto;
}

export enum BreadCrumbTypeDto {
  CHAPTER = 'CHAPTER',
  SECTION = 'SECTION',
  SUBSECTION = 'SUBSECTION',
}

export type BreadCrumbDto = {
  id: string;
  type: BreadCrumbTypeDto;
  title: string;
  page_range?: number[];
  cfi?: string;
}

export enum HMPlatformVectorContentTypeDto {
  EBK = 'EBK',
  BK = 'BK'
}

export type ChatReferenceBookChunkContentPropsDto = {
  page?: {
    end_page: {
      number: string;
      tag_id: string;
    };
    start_page: {
      number: string;
      tag_id: string;
    };
  };
  source_id: string;
  location?: string;
  bread_crumbs: BreadCrumbDto[];
  chapter_number: string;
  source_id_type: 'EEO-ISBN' | 'ISBN';
  isbn: string;
  content_type: HMPlatformVectorContentTypeDto;
  chunk_id: string;
  chunk_text: string;
  content_id: string;
  content_item_id: string;
  document_title: string;
  edition: string;
  eid: string;
  identifier_type: string;
  page_range: string;
  title: string;
}

export type ChatReferenceVideoContentPropsDto = {
  sections_video_muxThumbSmall: string;
  sections_video_muxThumb: string;
  source_id: string;
  metadata_videoObjectStructuredData: string;
  location: string;
  source_id_type: 'EEO-ISBN';
  url: string;
}

export type VideoObjectStructuredDataDto = {
  '@context': string;
  '@type': string;
  requiresSubscription: boolean;
  name: string;
  description: string;
  thumbnailUrl: string;
  transcript: boolean;
  publisher: {
    '@type': string;
    name: string;
    logo: {
      '@type': string;
      url: string;
    };
  };
  embedUrl: string;
  duration: string; // ISO 8601 duration format (e.g., "PT5M20S")
  uploadDate: string; // ISO 8601 date format
}

export type ChatReferenceDto = {
  id: number; // database id
  entryId: number;
  type: ChatReferenceTypeDto;
  referenceClass: ChatReferenceClassDto;
  title: string;
  content: string;
  parts?: ChatReferencePartDto[]; // this is a proposal so do not use this yet
  uri: string; // chapter/15/section/4/
  contentId: string;
  index: number;
  isVisible: boolean;
  confidenceScore: number;
  resourceType: ChatResourceTypeDto;
  resourceUri: string; // book/name_of_book/
  resourceId: string;
  resourceTitle: string;
  contentProps: string; // JSON string
}

export type ChatReferenceWithParsedBookContentProps = ChatReferenceDto & {
  _contentProps: ChatReferenceBookChunkContentPropsDto;
};

export enum ChatReferenceTypeDto {
  TEXT = 'TEXT',
  VIDEO = 'VIDEO',
}

export enum ChatResourceTypeDto {
  EBOOK = 'EBOOK',
  CASE_STUDY = 'CASE_STUDY',
  LESSON = 'LESSON',
  VIDEO = 'VIDEO',
}

export type ChatPaginationResponseDto = {
  currentPage: number;
  last: boolean;
  totalItems: number;
  totalPages: number;
}

export type ChatPerformanceDto = {
  studentCount: number;
  queryCount: number;
  perDayMetrics: {
    startDate: string;
    endDate: string;
    studentCount: number;
    queryCount: number;
  }[];
}

export type StudentThemesStatusDto = {
  request: {
    startDate: string;
    numOfDays: number;
    courseSectionIds: number[];
  };
  requestKey: string;
  status: StudentThemesStatus;
  response: StudentThemesResponseDto;
};

export type StudentThemesResponseDto = {
  chatThemes: {
    title: string;
    summary: string;
    sampleMessages: string[];
  }[];
}

export enum StudentThemesStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  INSUFFICIENT_ENTRIES = 'INSUFFICIENT_ENTRIES',
  ERROR = 'ERROR'
}

export type VectorSearchResponseDto = {
  query: string;
  results: {
    _source: {
      bread_crumbs: BreadCrumbDto[];
      content_item_id: string;
      document_title: string;
      eid: string;
      source_id: string;
      source_id_type: string;
      title: string;
      content_type: string;
      content_id: string;
      chunk_text: string;
      chunk_id: string;
      content_props: {
        chapter_number: string;
        page: {
          end_page: unknown;
          start_page: unknown;
        };
      };
    };
    _score: string;
  }[];
  success: boolean;
  number_of_hits: number;
  total_time: number;
  service_version: string;
  model_name: string;
}

export type LlmResponseDto = {
  model: string;
  created: number;
  provider: string;
  choices: Array<{
    index: number;
    finish_reason: string;
    message: {
      role: 'user' | 'assistant' | 'system';
      content: string;
    };
  }>;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

export enum LlmModels {
  /* eslint-disable-next-line camelcase */
  claude_3_sonnet = 'us.anthropic.claude-3-sonnet-20240229-v1:0',
  /* eslint-disable-next-line camelcase */
  claude_35_sonnet = 'us.anthropic.claude-sonnet-3-5-20240620-v1:0',
  /* eslint-disable-next-line camelcase */
  claude_3_haiku = 'us.anthropic.claude-3-haiku-20240307-v1:0',
  /* eslint-disable-next-line camelcase */
  claude_35_haiku = 'us.anthropic.claude-haiku-3-5-20241022-v1:0',
  /* eslint-disable-next-line camelcase */
  claude_4_sonnet = 'us.anthropic.claude-sonnet-4-5-20250929-v1:0',
  /* eslint-disable-next-line camelcase */
  claude_4_haiku = 'us.anthropic.claude-haiku-4-5-20251001-v1:0',
}

export type VectorSearchDto = {
  query_text: string;
  dsl_filter: RecordAny;
  number_of_results_to_provide: number;
  number_of_results_to_try: number;
  minimum_chunk_length: number;
  k: number;
}

export type LlmPromptDto = {
  model: LlmModels | string;
  temperature: number;
  messages: Array<{
    role: 'user' | 'assistant' | 'system';
    content: string;
  }>;
  stream?: boolean;
  performanceConfig?: {
    latency: 'STANDARD';
  };
}

export enum ChatEntitlementTypeDto {
  BASE_SET = 'BASE_SET',
  USER_ENTITLED = 'USER_ENTITLED',
  PILOT_COURSE = 'PILOT_COURSE',
  BANK_ENTITLED = 'BANK_ENTITLED',
  VST_ENTITLED = 'VST_ENTITLED',
}

export type ChatEntitlementDto = {
  id: number;
  isbn: string;
  isBaseSet: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  title: string;
  author: string;
  edition: number;
  pmg: string;
  pmc: string;
}

export type AdminChatIsbnBookDto = {
  id: number;
  isbn: string;
  specialty: string;
  discipline: string;
  isBaseSet: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  title: string;
  author: string;
  edition: number;
  editionText: string;
}

export type FollowUpQuestionDto = {
  id: number;
  entryId: number;
  question: string;
  questionOrder: number;
  isSelected: boolean;
  createdAt: string;
  updatedAt: string;
}

export enum ChatUserRoleDto {
  STUDENT = 'STUDENT',
  INSTRUCTOR = 'INSTRUCTOR',
  INSTRUCTOR_AS_STUDENT = 'INSTRUCTOR_AS_STUDENT',
}

export type VideoRelatedContentPropsDto = {
  '@type': 'OSMOSIS_VIDEO';
  learnUrl: string; // "https://zenon.dev.osmosis.org/learn/Antidiabetic_Therapy_-_Insulin"
  metadata_videoObjectStructuredData: string;
  sections_video_muxThumb: string;
  sections_video_muxThumbSmall: string;
  chunkIds: string[];
  // Recommended video start in whole seconds, set by NACS when video timestamps are enabled (FLOR-3180)
  startTimeSeconds?: number;
}

export type VectorContentPropsDto = {
  page: {
    end_page: {
      number: string;
      tag_id: string;
    };
    start_page: {
      number: string;
      tag_id: string;
    };
  };
  caption: string;
  context: string;
  locator: string;
  copyright: string;
  image_IRI: string;
  asset_type: string;
  description: string;
  annotation_IRI: string;
  chapter_number: string;
}

export type ImageChunkPropsDto = {
  chunkText: string;
  description?: string;
  caption?: string;
  context?: string;
  breadCrumbs: BreadCrumbDto[];
  ebookData: {
    isbn: string;
    title: string;
  };
  vectorContentProps?: VectorContentPropsDto;
}

export type ImageRelatedContentPropsDto = {
  '@type': 'IMAGE';
  chunkIds: string[];
  chunkIdToProps: Record<string, ImageChunkPropsDto>;
}

export type RelatedContentDto = {
  id: number;
  chatId: number;
  entryId: number;
  confidenceScoreList: number[];
  rating: number;
  contentType: RelatedContentType;
  contentId: string;
  title: string;
  searchField: string;
  searchValue: string;
  contentProps: VideoRelatedContentPropsDto | ImageRelatedContentPropsDto;
  entitledBy: string;
  createdAt: string;
  updatedAt: string;
}

export enum RelatedContentType {
  IM = 'IM',
  OST = 'OST',
}


export type StreamPromptRequestDto = {
  userEntryId: number;
  botEntryId: number;
  promptOverride?: PromptOverrideDto;
  searchOverride?: SearchOverrideDto;
  context?: EntryContextDto;
  isOsmosis: boolean;
}

export type FlashcardDto = {
  id: number;
  entryId: number;
  chatId: number;
  frontText: string;
  backText: string;
  index: number;
  references: ChatReferenceDto[];
  createdAt: string;
  updatedAt: string;
}

export type FlashcardResponseDto = {
  botEntryId: number;
  chatId: number;
  flashcards: FlashcardDto[];
}

export type FlashcardSelectionDto = LibrarySelectionDto;

export type FlashcardListParamsDto = LibraryQueryParamsDto;

export type FlashcardListItemDto = FlashcardDto & {
  chatTitle: string;
  // The user message the flashcard was generated from
  entryText: string;
};

export type FlashcardListResponseDto = LibraryListResponseDto<FlashcardListItemDto>;

export type ContextRequestDto = {
  id: string;
  type: ChatContextTypeDto;
}
